import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CONTRACT_STATUSES,
  CONTRACT_STATUS_LABELS,
  canOpenContract,
  canOpenInvoice,
  formatDate,
  formatHours,
  formatMonthYear,
  type CompanyCheck,
  type InvoiceOverview,
  type Paginated,
} from '@codepass/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { useCompanyChecks } from '@/lib/company-checks';
import { CompanyCheckDialog, CompanyRatingButton } from '@/components/CompanyCheck';
import { InvoiceScheduleCard } from '@/components/InvoiceScheduleCard';
import { InvoiceRulesCard } from '@/components/InvoiceRulesCard';
import { MonthlyHoursCard } from '@/components/MonthlyHoursCard';
import { contractPath, invoicePath } from '@/lib/paths';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  MultiSelect,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import {
  ContractStatusBadge,
  ContractTypeBadge,
  dsoHint,
  EmptyState,
  InvoiceBasisBadge,
  InvoiceStatusBadge,
  MetricCard,
  Money,
  PageHeader,
  Pagination,
  PaymentTerms,
  PermittedLink,
} from '@/components/domain';
import type {
  ContractListItem,
  CustomerInvoiceItem,
  CustomerListItem,
  CustomerStatistics,
} from '@/types';

interface CustomerDetailResponse extends CustomerListItem {
  contracts: ContractListItem[];
}

/**
 * Sidestoerrelsen i begge tabeller paa siden.
 *
 * Ti og ikke de halvtreds, listesiderne bruger: de to tabeller staar i samme spalte, og en
 * kunde med mange aftaler skubbede foer Fakturahistorik saa langt ned, at den ikke var til at
 * finde. Samme tal som /kontrakter, saa en kontrakttabel ser ens ud, hvor man end moeder den.
 */
const LIMIT = 10;

/** Every invoice in the overview, in any group. */
function invoiceCount(overview: InvoiceOverview): number {
  return (
    overview.drafts.count +
    overview.open.count +
    overview.overdue.count +
    overview.paid.count +
    overview.credited.count
  );
}

function fakturaer(count: number): string {
  return count === 1 ? '1 faktura' : `${count} fakturaer`;
}

/**
 * Kundens fakturaer i fem grupper, med antal og beloeb ekskl. moms - raekken under
 * Fakturahistorik. Alle kundens fakturaer, ikke kun tabellens side. Grupperne regnes af
 * `invoiceOverview` i @codepass/shared; her tegnes de kun.
 */
function InvoiceOverviewRow({ overview }: { overview: InvoiceOverview }) {
  const { drafts, open, overdue, paid, credited } = overview;
  const tiles: Array<{
    label: string;
    group: { count: number; amount_excl_vat: number };
    note?: string;
    danger?: boolean;
  }> = [
    { label: 'Kladder', group: drafts, note: 'ikke sendt' },
    {
      label: 'Åbne',
      group: open,
      note: open.partly_paid > 0 ? `${open.partly_paid} delvist betalt` : 'ikke forfaldne',
    },
    {
      label: 'Forfaldne',
      group: overdue,
      note:
        overdue.oldest_days !== null
          ? `ældste ${overdue.oldest_days} ${overdue.oldest_days === 1 ? 'dag' : 'dage'} over`
          : 'over forfald',
      danger: overdue.count > 0,
    },
    { label: 'Betalt', group: paid },
    { label: 'Krediteret', group: credited },
  ];

  return (
    <div className="border-b border-line px-4 py-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-md border border-line px-3 py-2">
            <p className="text-xs text-ink-muted">{tile.label}</p>
            <p className={`mt-0.5 text-sm font-semibold ${tile.danger ? 'text-danger' : ''}`}>
              <Money value={tile.group.amount_excl_vat} currency={overview.currency} />
            </p>
            <p className="text-xs text-ink-muted">
              {fakturaer(tile.group.count)}
              {tile.note && tile.group.count > 0 ? ` · ${tile.note}` : ''}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-ink-muted">
        Alle kundens fakturaer, ekskl. moms. Åbne og forfaldne står med det, der mangler at blive
        betalt.
      </p>
    </div>
  );
}

export function CustomerDetailPage() {
  /* Contracts and invoices link only for a role that can open them - see `PermittedLink`. */
  const { user } = useAuth();
  /**
   * Statusfilteret over kontrakttabellen.
   *
   * Erstatter de grupperaekker der delte tabellen op i Aktive, Afventer signatur, Afsluttede og
   * Annullerede. Grupperne viste altid alle fire; filteret lader kunden vise netop det man er
   * ude efter - og en kunde med tyve aftaler bliver laesbar.
   *
   * Tom liste betyder alle, som paa /kontrakter og /timer. Samme komponent og samme
   * "Alle statusser"-tekst, saa filtrene i appen opfoerer sig ens.
   */
  const [statuses, setStatuses] = useState<string[]>([]);
  /**
   * Hver tabel har sin egen side, og de to hoerer ikke sammen: at bladre i fakturaerne maa
   * ikke flytte kontrakttabellen.
   *
   * Kontrakterne bladres i klienten - `GET /customers/:id` leverer dem alle sammen, fordi
   * statusfilteret og indrykningen af tillaegsaftaler begge skal kunne se hele listen.
   * Fakturaerne bladres paa serveren, som allerede tager limit og offset.
   */
  const [contractOffset, setContractOffset] = useState(0);
  const [invoiceOffset, setInvoiceOffset] = useState(0);
  // Noeglen er kundens CVR, med UUID som fallback. API'et slaar begge op, saa den kan gaa
  // videre raat - se customerPath i lib/paths.ts og customerKey i routes/customers.ts.
  const { id = '' } = useParams();

  /*
   * Samme komponent genbruges, naar man gaar fra én kunde til en anden - fx via en alarm - saa
   * uden det her ville side 3 hos den forrige kunde blive til side 3 hos den naeste.
   */
  useEffect(() => {
    setStatuses([]);
    setContractOffset(0);
    setInvoiceOffset(0);
  }, [id]);

  const {
    data: customer,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get<CustomerDetailResponse>(`/customers/${id}`),
    enabled: Boolean(id),
  });

  const { data: statistics } = useQuery({
    queryKey: ['customer', id, 'statistics'],
    queryFn: () => api.get<CustomerStatistics>(`/customers/${id}/statistics`),
    enabled: Boolean(id),
  });

  const { data: invoices } = useQuery({
    queryKey: ['customer', id, 'invoices', invoiceOffset],
    queryFn: () =>
      api.get<Paginated<CustomerInvoiceItem>>(`/customers/${id}/invoices`, {
        limit: LIMIT,
        offset: invoiceOffset,
      }),
    enabled: Boolean(id),
    /** Den forrige side bliver staaende mens den naeste hentes - ingen blinkende tabel. */
    placeholderData: (previous) => previous,
  });

  // The same answer as Status on /kunder - see useCompanyChecks.
  const { data: checks } = useCompanyChecks();
  const [openCheck, setOpenCheck] = useState<CompanyCheck | null>(null);
  const companyCheck = checks?.data.find((check) => check.customer_id === customer?.id);

  if (isLoading) return <Skeleton className="h-64" />;
  if (error || !customer) return <Alert tone="error">Kunne ikke hente kunden.</Alert>;

  /*
   * Serviceaftalerne staar i deres eget kort, som paa /kontrakter?kind=service: de har hverken
   * kontraktsum, slutdato eller tillaeg, og deres tal er timepris og timer pr. opgave. I
   * kontrakttabellen ville de staa med en sum, der i virkeligheden er en timepris.
   */
  const projectContracts = customer.contracts.filter((contract) => !contract.service_agreement);
  const serviceAgreements = customer.contracts.filter((contract) => contract.service_agreement);

  // Tillaegsaftaler vises indrykket under deres moderaftale, saa kaeden er synlig - ogsaa
  // naar tillaegget har en anden status end aftalen. Kaeden siger mere end status gør.
  const amendmentsByParent = new Map<string, ContractListItem[]>();
  for (const contract of projectContracts) {
    if (!contract.parent_contract_id) continue;
    const list = amendmentsByParent.get(contract.parent_contract_id) ?? [];
    list.push(contract);
    amendmentsByParent.set(contract.parent_contract_id, list);
  }

  const dsoDays = statistics?.average_dso_days ?? null;

  /*
   * Kun moderaftaler i tabellen; tillaeg vises indrykket under deres egen. Filteret gaelder
   * derfor moderaftalens status - et tillaeg forsvinder ikke ud af kaeden, fordi det selv har
   * en anden status end den aftale det hoerer til.
   */
  const primary = projectContracts.filter((contract) => !contract.parent_contract_id);
  const visible =
    statuses.length === 0
      ? primary
      : primary.filter((contract) => statuses.includes(contract.status));

  /*
   * Sideinddelingen taeller moderaftaler, ikke raekker: et tillaeg foelger sin egen aftale og
   * maa ikke havne paa den naeste side, hvor kaeden ville staa uden sit ophaeng.
   */
  const contractPage = visible.slice(contractOffset, contractOffset + LIMIT);

  return (
    <>
      <PageHeader
        title={customer.name}
        description={
          customer.cvr
            ? `CVR ${customer.cvr}`
            : customer.country_code !== 'DK'
              ? `${customer.country_code} — reverse charge`
              : undefined
        }
        actions={
          <>
            {customer.archived ? <Badge tone="neutral">Arkiveret i Dynamics</Badge> : null}
            {companyCheck ? (
              <CompanyRatingButton check={companyCheck} onOpen={setOpenCheck} />
            ) : null}
          </>
        }
      />
      <CompanyCheckDialog check={openCheck} onClose={() => setOpenCheck(null)} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Aktive kontrakter" value={customer.active_contract_count} />
        <MetricCard
          label="Faktureret i år"
          value={
            <Money value={customer.billed_ytd_excl_vat} currency={customer.currency} decimals={0} />
          }
        />
        <MetricCard
          label="Faktureret i alt"
          value={
            <Money
              value={statistics?.total_billed_excl_vat ?? 0}
              currency={customer.currency}
              decimals={0}
            />
          }
          hint={statistics ? `${statistics.invoice_count} fakturaer` : undefined}
        />
        <MetricCard
          label="Gns. DSO"
          value={dsoDays === null ? '—' : `${Math.round(dsoDays)} dage`}
          /*
           * Samme linje som paa dashboardets KPI: kundens faktiske betalingstid fra udligningerne
           * i BC, eller et estimat, der skal sige det. Se OQ-10.
           */
          hint={
            statistics
              ? dsoHint(statistics.dso_is_estimated, statistics.dso_payment_count)
              : undefined
          }
        />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader className="flex items-center justify-between gap-3">
              <CardTitle>Kontrakter</CardTitle>
              {/*
               * Kun de statusser kunden faktisk har. Et valg der altid giver en tom tabel er
               * ikke et filter men en blindgyde - samme regel som filtervalgene paa /alarmer.
               */}
              <div className="w-[190px] shrink-0">
                <MultiSelect
                  values={statuses}
                  // Et aendret filter goer den aktuelle side meningsloes - start forfra.
                  onChange={(next) => {
                    setStatuses(next);
                    setContractOffset(0);
                  }}
                  options={CONTRACT_STATUSES.filter((status) =>
                    primary.some((contract) => contract.status === status),
                  ).map((status) => ({ value: status, label: CONTRACT_STATUS_LABELS[status] }))}
                  allLabel="Alle statusser"
                />
              </div>
            </CardHeader>
            {visible.length === 0 ? (
              <EmptyState
                title={
                  primary.length === 0
                    ? 'Ingen kontrakter på kunden'
                    : 'Ingen kontrakter med den valgte status'
                }
              />
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <Th>Kontrakt</Th>
                      <Th className="w-[130px]">Type</Th>
                      <Th className="w-[130px]">Status</Th>
                      <Th className="w-[140px] text-right">Sum</Th>
                      <Th className="w-[110px]">Slutdato</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractPage.map((contract) => (
                      <ContractRows
                        key={contract.id}
                        contract={contract}
                        amendments={amendmentsByParent.get(contract.id) ?? []}
                      />
                    ))}
                  </tbody>
                </Table>
                {/* Taeller de filtrerede moderaftaler, ikke alle kundens - saa "af 7" er de
                    syv, filteret faktisk viser. */}
                <Pagination
                  total={visible.length}
                  limit={LIMIT}
                  offset={contractOffset}
                  onChange={setContractOffset}
                />
              </>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Serviceaftaler</CardTitle>
            </CardHeader>
            {serviceAgreements.length === 0 ? (
              <EmptyState
                title="Ingen serviceaftaler på kunden"
                description="En serviceaftale er en aftale med Kontrakt type = Serviceaftale i CRM."
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Serviceaftale</Th>
                    <Th className="w-[130px]">Status</Th>
                    <Th className="w-[140px] text-right">Timepris</Th>
                    <Th
                      className="w-[140px] text-right"
                      title="Det, en opgave må bruge, før timerne faktureres"
                    >
                      Timer pr. opgave
                    </Th>
                    <Th className="w-[110px]">Signeret</Th>
                  </tr>
                </thead>
                <tbody>
                  {serviceAgreements.map((agreement) => {
                    const rate = agreement.service_agreement?.hourly_rate_excl_vat ?? null;
                    return (
                      <Tr key={agreement.id}>
                        <Td>
                          <PermittedLink
                            to={contractPath(agreement)}
                            allowed={canOpenContract(user, agreement)}
                            className="font-medium hover:underline"
                          >
                            {agreement.title}
                          </PermittedLink>
                          {agreement.contract_no ? (
                            <span className="mt-0.5 block text-xs tabular text-ink-muted">
                              {agreement.contract_no}
                            </span>
                          ) : null}
                        </Td>
                        <Td>
                          <ContractStatusBadge status={agreement.status} />
                        </Td>
                        <Td className="text-right">
                          {rate === null ? (
                            <span className="text-xs text-ink-muted">—</span>
                          ) : (
                            <Money value={rate} currency={agreement.currency} />
                          )}
                        </Td>
                        <Td className="text-right tabular">
                          {formatHours(agreement.service_agreement?.hours_per_task ?? null)}
                        </Td>
                        <Td className="text-xs tabular">{formatDate(agreement.signed_date)}</Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Fakturahistorik</CardTitle>
            </CardHeader>
            {statistics?.invoice_overview && invoiceCount(statistics.invoice_overview) > 0 ? (
              <InvoiceOverviewRow overview={statistics.invoice_overview} />
            ) : null}
            {!invoices || invoices.data.length === 0 ? (
              <EmptyState
                title="Ingen fakturahistorik"
                description="Historikken viser fakturaer, der er sendt til Business Central. Kladder står på /fakturering."
              />
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <Th className="w-[110px]">Dato</Th>
                      <Th className="w-[150px]">Faktura</Th>
                      <Th>Kontrakt</Th>
                      <Th className="w-[130px]">Grundlag</Th>
                      <Th className="w-[120px]">Status</Th>
                      <Th className="w-[140px] text-right">Beløb ekskl. moms</Th>
                      <Th className="w-[110px]">Forfald</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.data.map((invoice) => (
                      <Tr key={invoice.id}>
                        <Td className="text-xs tabular">{formatDate(invoice.created_at)}</Td>
                        <Td>
                          <PermittedLink
                            to={invoicePath(invoice)}
                            allowed={canOpenInvoice(user, invoice.status)}
                            className="font-medium hover:underline"
                          >
                            {invoice.bc_invoice_id ?? invoice.invoice_no_proposal ?? 'Uden nummer'}
                          </PermittedLink>
                        </Td>
                        <Td>
                          {/*
                           * Kontrakten udledes af referencen i BC. En faktura bogholderiet selv
                           * har oprettet har ingen, og den skal stadig kunne staa i historikken
                           * - beloebet er faktureret uanset om Codepass kender aftalen.
                           */}
                          {invoice.contract ? (
                            <PermittedLink
                              to={contractPath(invoice.contract)}
                              allowed={canOpenContract(user, invoice.contract)}
                              className="hover:underline"
                            >
                              {invoice.contract.title}
                            </PermittedLink>
                          ) : (
                            <span className="text-xs text-ink-muted">Uden kontrakt</span>
                          )}
                        </Td>
                        <Td>
                          {invoice.invoice_basis ? (
                            <InvoiceBasisBadge basis={invoice.invoice_basis} />
                          ) : (
                            <span className="text-xs text-ink-muted">—</span>
                          )}
                        </Td>
                        <Td>
                          <InvoiceStatusBadge status={invoice.status} payment={invoice} />
                        </Td>
                        <Td className="text-right">
                          <Money
                            value={invoice.amount_excl_vat}
                            currency={invoice.contract?.currency ?? invoice.currency}
                            decimals={0}
                          />
                        </Td>
                        <Td className="text-xs tabular">{formatDate(invoice.due_date)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
                <Pagination
                  total={invoices.total}
                  limit={LIMIT}
                  offset={invoiceOffset}
                  onChange={setInvoiceOffset}
                />
              </>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          {/* Hvornår kunden får sin faktura: Manuelt, hver halve måned eller hver måned. */}
          <InvoiceScheduleCard customerKey={id} />

          {/* Mindste antal timer, fast retainer og tidsregistrering som Excel. */}
          <InvoiceRulesCard customerKey={id} />

          <Card>
            <CardHeader>
              <CardTitle>Kundeoplysninger</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm">
              <Field label="CVR" value={customer.cvr ?? '—'} />
              <Field label="Land" value={customer.country_code} />
              <Field label="Valuta" value={customer.currency} />
              <Field
                label="Betalingsbetingelser"
                value={
                  <PaymentTerms
                    days={customer.payment_terms_days}
                    issue={customer.payment_terms_issue}
                  />
                }
                hint={customer.country_code !== 'DK' ? 'Reverse charge' : undefined}
              />
              <Field
                label="Email"
                value={
                  customer.contact_email ? (
                    <a
                      href={`mailto:${customer.contact_email}`}
                      className="text-brand hover:underline"
                    >
                      {customer.contact_email}
                    </a>
                  ) : (
                    '—'
                  )
                }
              />
              <Field label="BC-kundenummer" value={customer.bc_customer_no ?? '—'} />
              {/*
               * Hvert felt i kortet har en kilde i Business Central: CVR fra
               * taxRegistrationNumber, land og valuta fra kundekortet med selskabets egne som
               * standard, betalingsbetingelser fra paymentTerms, email og kundenummer fra
               * kundekortet. Felter uden en kilde i BC hoerer ikke her - se bc-customers.ts.
               */}
              <p className="pt-1 text-xs text-ink-muted">
                Read-only. Kunden vedligeholdes i Business Central og læses live derfra.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Største kontrakt</CardTitle>
            </CardHeader>
            <CardBody>
              {!statistics?.largest_contract ? (
                <p className="text-xs text-ink-muted">Ingen kontrakter på kunden.</p>
              ) : (
                <div className="space-y-1 text-sm">
                  <PermittedLink
                    to={contractPath(statistics.largest_contract)}
                    allowed={canOpenContract(user, statistics.largest_contract)}
                    className="font-medium hover:underline"
                  >
                    {statistics.largest_contract.title}
                  </PermittedLink>
                  <p>
                    <Money
                      value={statistics.largest_contract.contract_sum_excl_vat}
                      currency={statistics.largest_contract.currency}
                      decimals={0}
                    />
                  </p>
                  <p className="text-xs text-ink-muted">
                    {CONTRACT_STATUS_LABELS[statistics.largest_contract.status]}
                  </p>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Omsætning pr. måned</CardTitle>
            </CardHeader>
            <CardBody>
              {!statistics || statistics.by_month.length === 0 ? (
                <p className="text-xs text-ink-muted">Ingen fakturering registreret.</p>
              ) : (
                <ul className="space-y-1 text-xs">
                  {statistics.by_month.slice(-12).map((month) => (
                    <li key={month.month} className="flex justify-between gap-2">
                      <span className="text-ink-muted">{formatMonthYear(`${month.month}-01`)}</span>
                      <Money
                        value={month.billed_excl_vat}
                        currency={customer.currency}
                        decimals={0}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* Registreret tid i OnePro pr. måned - se customer-hours.ts. */}
          <MonthlyHoursCard customerKey={id} />
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
}) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-xs text-ink-muted">{label}</span>
      <span className="text-right">
        {value}
        {hint ? <span className="block text-xs text-ink-muted">{hint}</span> : null}
      </span>
    </div>
  );
}

function ContractRows({
  contract,
  amendments,
}: {
  contract: ContractListItem;
  amendments: ContractListItem[];
}) {
  const { user } = useAuth();
  return (
    <>
      <Tr>
        <Td>
          <PermittedLink
            to={contractPath(contract)}
            allowed={canOpenContract(user, contract)}
            className="font-medium hover:underline"
          >
            {contract.title}
          </PermittedLink>
        </Td>
        <Td>
          <ContractTypeBadge type={contract.contract_type} />
        </Td>
        <Td>
          <ContractStatusBadge status={contract.status} />
        </Td>
        <Td className="text-right">
          <Money value={contract.contract_sum_excl_vat} currency={contract.currency} decimals={0} />
        </Td>
        <Td className="text-xs tabular">{formatDate(contract.end_date)}</Td>
      </Tr>
      {amendments.map((amendment) => (
        <Tr key={amendment.id}>
          <Td className="pl-8">
            <span className="text-ink-muted">↳ </span>
            <PermittedLink
              to={contractPath(amendment)}
              allowed={canOpenContract(user, amendment)}
              className="hover:underline"
            >
              {amendment.title}
            </PermittedLink>
          </Td>
          <Td>
            <ContractTypeBadge type={amendment.contract_type} />
          </Td>
          <Td>
            <ContractStatusBadge status={amendment.status} />
          </Td>
          <Td className="text-right">
            <Money
              value={amendment.contract_sum_excl_vat}
              currency={amendment.currency}
              decimals={0}
            />
          </Td>
          <Td className="text-xs tabular">{formatDate(amendment.end_date)}</Td>
        </Tr>
      ))}
    </>
  );
}
